<template>
  <section id="packages" class="packages-section" :class="{ 'packages-section--standalone': standalone }">
    <v-container>
      <div class="section-heading">
        <div class="eyebrow">Featured packages</div>
        <h2>Thoughtfully designed health packages for routine care, employment requirements, and ongoing wellness.</h2>
        <p>
          Explore Packages 1–4 and compare their prices, savings, and complete test inclusions.
        </p>
      </div>

      <v-row align="stretch">
        <v-col v-for="item in packages" :key="item.name" cols="12" md="6" lg="4">
          <v-card class="package-card" elevation="0">
            <div class="package-card__content">
              <div class="package-card__badge">{{ item.badge }}</div>
              <h3>{{ item.name }}</h3>
              <p class="package-card__description">{{ item.description }}</p>
              <div class="package-card__price">{{ item.price }}</div>
              <div class="package-card__value-row">
                <span>Regular {{ item.regularPrice }}</span>
                <strong>Save {{ item.savings }}</strong>
              </div>
              <div class="package-card__best-for">Best for: {{ item.bestFor }}</div>
              <ul class="package-card__includes">
                <li v-for="included in item.includes" :key="included">
                  <span class="check-icon">✓</span>
                  {{ included }}
                </li>
              </ul>
            </div>

            <div class="package-card__actions">
              <v-btn class="package-card__button mayon-btn mayon-btn--primary mayon-btn--block" block to="/branches">
                Inquire about package
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-alert text color="#4870c8" class="package-disclaimer">
        Package prices and discount eligibility are based on the provided MayonLab package sheet. Please confirm final pricing, preparation requirements, and availability with the branch before visiting.
      </v-alert>

      <div class="section-links">
        <router-link to="/packages" v-if="!standalone">View all packages</router-link>
        <router-link to="/services">Browse services</router-link>
        <router-link to="/hmo-partners">Review HMO partners</router-link>
        <router-link to="/branches">Contact branches</router-link>
      </div>
    </v-container>
  </section>
</template>

<script>
import { packages } from '@/data/mayonSite'

export default {
  name: 'PackagedService',
  props: {
    standalone: { type: Boolean, default: false }
  },
  data() {
    return {
      packages
    }
  }
}
</script>

<style lang="scss" scoped>
.packages-section {
  background: linear-gradient(180deg, #ffffff 0%, var(--mayon-bg) 100%);
  padding: 5rem 0;
}

.section-heading {
  margin: 0 auto 2.25rem;
  max-width: 760px;
  text-align: center;

  .eyebrow {
    color: var(--mayon-blue);
    font-size: 0.82rem;
    font-weight: 800;
    letter-spacing: 0.09em;
    margin-bottom: 0.7rem;
    text-transform: uppercase;
  }

  h2 {
    color: var(--mayon-navy);
    font-size: clamp(2rem, 4vw, 3rem);
    letter-spacing: -0.035em;
    line-height: 1.1;
    margin-bottom: 1rem;
  }

  p {
    color: var(--mayon-muted);
    font-size: 1rem;
    line-height: 1.8;
  }
}

.package-card {
  background: #ffffff !important;
  border: 1px solid var(--mayon-border) !important;
  border-radius: 24px !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 690px;
  padding: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    border-color: rgba(72, 112, 200, 0.24) !important;
    box-shadow: var(--mayon-shadow-card) !important;
    transform: translateY(-3px);
  }

  &__content {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
  }

  &__badge {
    align-self: flex-start;
    background: rgba(88, 184, 184, 0.10);
    border: 1px solid rgba(88, 184, 184, 0.24);
    border-radius: 999px;
    color: var(--mayon-blue);
    display: inline-flex;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.07em;
    margin-bottom: 1rem;
    padding: 0.35rem 0.7rem;
    text-transform: uppercase;
  }

  h3 {
    color: var(--mayon-navy);
    font-size: 1.25rem;
    margin-bottom: 0.65rem;
    min-height: 3rem;
  }

  &__description {
    color: var(--mayon-muted);
    line-height: 1.7;
    margin-bottom: 1rem;
    min-height: 78px;
  }

  &__price {
    color: var(--mayon-navy);
    font-size: 1.9rem;
    font-weight: 900;
    line-height: 1.1;
    margin: 0 0 0.35rem;
    min-height: 2.2rem;
  }

  &__value-row {
    align-items: center;
    color: var(--mayon-muted);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.84rem;
    gap: 0.45rem 0.8rem;
    margin-bottom: 0.8rem;

    span {
      text-decoration: line-through;
    }

    strong {
      color: var(--mayon-teal-deep);
    }
  }

  &__best-for {
    color: var(--mayon-muted);
    font-size: 0.86rem;
    font-weight: 700;
    margin-bottom: 1rem;
    min-height: 2.4rem;
  }

  &__includes {
    flex: 1 1 auto;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__actions {
    margin-top: auto;
    padding-top: 1.35rem;
  }

  &__button {
    min-height: 50px !important;
  }

  .check-icon {
    align-items: center;
    background: rgba(88, 184, 184, 0.14);
    border-radius: 50%;
    color: var(--mayon-teal-deep);
    display: inline-flex;
    flex: 0 0 20px;
    font-size: 0.75rem;
    font-weight: 900;
    height: 20px;
    justify-content: center;
    width: 20px;
  }

  li {
    align-items: flex-start;
    color: #344054;
    display: flex;
    gap: 0.45rem;
    line-height: 1.55;
    margin-bottom: 0.45rem;
  }
}

.section-links {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.25rem;

  a {
    color: var(--mayon-blue);
    font-weight: 800;
    text-decoration: none;
  }
}

.package-disclaimer {
  border-radius: 18px !important;
  margin-top: 1.5rem;
}

@media (max-width: 600px) {
  .package-card {
    min-height: auto;

    h3,
    &__description,
    &__value-row {
    align-items: center;
    color: var(--mayon-muted);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.84rem;
    gap: 0.45rem 0.8rem;
    margin-bottom: 0.8rem;

    span {
      text-decoration: line-through;
    }

    strong {
      color: var(--mayon-teal-deep);
    }
  }

  &__best-for {
      min-height: auto;
    }
  }
}
</style>
